import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Upload, Landmark, FlaskConical, Clock, LifeBuoy } from "lucide-react";
import { api, errMsg, fmt, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Field, PageTitle, btnGhost, btnPrimary, inputCls } from "../components/ui";
import ReceiptLink from "../components/ReceiptLink";

export const TOPUP_STATUS = { pending_payment: "Aguarda pagamento", payment_submitted: "Comprovativo enviado", payment_verifying: "Pagamento em verificação",
  paid: "Pagamento confirmado", processing: "Recarga em processamento", completed: "Recarga concluída", failed: "Recarga falhada",
  rejected: "Rejeitado", cancelled: "Cancelado" };
const OPS = { cst: { name: "CST", color: "bg-sky-600" }, unitel: { name: "Unitel", color: "bg-orange-500" } };
export const DEMO_TEXT = "DEMONSTRAÇÃO — sem pagamentos nem recargas reais";

export const DemoBadge = () => <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-extrabold uppercase text-bo-ink" data-testid="topup-demo-badge">DEMO</span>;

export const DemoBanner = ({ testid = "topup-demo-banner" }) => (
  <div className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 p-3 text-sm font-bold text-amber-900" data-testid={testid}>
    <FlaskConical className="h-4 w-4 shrink-0" />{DEMO_TEXT}
  </div>
);

function TransferBox({ t, transfer }) {
  if (!transfer) return <p className="text-amber-700" data-testid="topup-transfer-missing">Instruções de pagamento indisponíveis. Contacte o apoio antes de transferir.</p>;
  return (
    <div className="flex flex-col gap-1" data-testid="topup-transfer-box">
      {transfer.bank_accounts.map((b) => <p key={b.iban} className="flex gap-2"><Landmark className="h-3.5 w-3.5" />{b.bank_name} · {b.holder} · <span className="font-mono">{b.iban}</span></p>)}
      <p>Referência do pedido (indique-a na transferência): <strong data-testid="topup-payment-reference">{t.payment_reference || t.number}</strong></p>
      {transfer.general_instructions && <p className="whitespace-pre-line">{transfer.general_instructions}</p>}
      {transfer.customer_text && <p className="whitespace-pre-line">{transfer.customer_text}</p>}
      {t.payment_deadline && <p data-testid="topup-deadline">Prazo de pagamento: {fmtDate(t.payment_deadline)}</p>}
      {transfer.receipt_guidance && <p className="text-gray-600">{transfer.receipt_guidance}</p>}
      {transfer.support_contact && <p className="text-gray-600">Apoio: {transfer.support_contact}</p>}
    </div>
  );
}

function TopupRow({ t, transfer, reload, demo }) {
  const ref = useRef();
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const form = new FormData(); form.append("file", file);
    try { await api.post(`/topups/${t.id}/receipt`, form); toast.success("Comprovativo enviado. Aguardamos a verificação do pagamento."); reload(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const cancel = async () => { try { await api.post(`/topups/${t.id}/cancel`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const simulate = async () => { try { await api.post(`/topups/${t.id}/simulate-payment`); toast.success("Pagamento DEMO simulado"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="bo-card flex flex-col gap-2 p-4 text-sm" data-testid={`topup-row-${t.number}`}>
      <div className="flex flex-wrap items-center gap-3">
        <strong>{t.number}</strong>{t.is_demo && <DemoBadge />}<span>{OPS[t.operator].name} · {t.phone} · {fmt(t.amount)}</span>
        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold" data-testid="topup-status">{TOPUP_STATUS[t.status] || t.status}</span>
        <span className="ml-auto font-bold">{fmt(t.price)}</span>
      </div>
      {t.status === "payment_submitted" && <p className="text-xs text-sky-700" data-testid="topup-receipt-sent-note">Comprovativo enviado. Aguardamos a verificação do pagamento.</p>}
      {t.reference && <p className="text-xs text-bo-green">Referência da recarga: {t.reference}</p>}
      {t.is_demo && demo && t.status === "pending_payment" && (
        <div className="flex flex-col gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-900" data-testid="topup-demo-actions">
          <p>{DEMO_TEXT}. Não transfira dinheiro: nenhum valor é cobrado e nenhuma operadora é contactada.</p>
          <div className="flex gap-2">
            <button onClick={simulate} className={`${btnPrimary} px-3 py-1.5`} data-testid="topup-simulate-btn">Simular pagamento</button>
            <button onClick={cancel} className={`${btnGhost} px-3 py-1.5`} data-testid="topup-demo-cancel-btn">Cancelar</button>
          </div>
        </div>
      )}
      {!t.is_demo && t.status === "pending_payment" && (
        <div className="rounded-xl bg-bo-bg p-3 text-xs">
          <TransferBox t={t} transfer={transfer} />
          <p className="mt-1">A recarga só é executada depois da verificação manual do pagamento recebido.</p>
          <input ref={ref} type="file" accept=".pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="topup-receipt-input" />
          <div className="mt-2 flex gap-2">
            {transfer && <button onClick={() => ref.current.click()} className={`${btnPrimary} px-3 py-1.5`} data-testid="topup-upload-btn"><Upload className="h-3.5 w-3.5" />Enviar comprovativo</button>}
            <button onClick={cancel} className={`${btnGhost} px-3 py-1.5`} data-testid="topup-cancel-btn">Cancelar</button>
          </div>
        </div>
      )}
      {t.receipts?.map((r) => <ReceiptLink key={r.id} r={r} />)}
    </div>
  );
}

function OperatorInfo({ o }) {
  const i = o.instructions;
  return (
    <div className="flex flex-col gap-1 rounded-xl bg-bo-bg p-3 text-xs text-gray-700" data-testid="topup-operator-info">
      {i.payment && <p className="whitespace-pre-line" data-testid="topup-instructions-payment">{i.payment}</p>}
      {i.operational && <p className="whitespace-pre-line">{i.operational}</p>}
      {i.eta && <p className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Prazo estimado: {i.eta}</p>}
      {i.support && <p className="flex items-center gap-1"><LifeBuoy className="h-3.5 w-3.5" />Apoio: {i.support}</p>}
      {(o.min_amount > 0 || o.max_amount > 0) && <p>Limites por pedido: {fmt(o.min_amount)} – {o.max_amount ? fmt(o.max_amount) : "sem máximo"}</p>}
    </div>
  );
}

export default function Recargas() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [op, setOp] = useState(null);
  const [phone, setPhone] = useState("+239");
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState({ topups: [], transfer: null });
  const loadMine = () => user && api.get("/topups/mine").then(({ data }) => setMine(data));
  useEffect(() => { api.get("/topups/packages").then(({ data }) => { setCfg(data); setOp(Object.keys(OPS).find((k) => data.operators[k]?.visible) || null); }); }, []);
  useEffect(() => { loadMine(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const visible = cfg ? Object.keys(OPS).filter((k) => cfg.operators[k]?.visible) : [];
  const o = op ? cfg.operators[op] : null;
  const pkgs = o?.packages || [];
  const sel = pkgs.find((p) => p.id === pkg);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/topups", { operator: op, phone, package_id: sel.id, amount: sel.amount, price: sel.price, idempotency_key: `${op}-${sel.id}-${phone}-${Date.now()}` });
      toast.success(cfg.demo ? "Pedido DEMO criado" : "Pedido criado: aguarda pagamento"); setPkg(null); loadMine();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-5" data-testid="topup-page">
      <PageTitle title="Recargas Digitais" sub="Recargas CST e Unitel processadas manualmente pela equipa BÔ Market após a verificação do pagamento." />
      {cfg?.demo && <DemoBanner />}
      {cfg && !visible.length && <p className="bo-card p-5 text-sm text-gray-600" data-testid="topup-unavailable">As recargas estão temporariamente indisponíveis.</p>}
      {o && (
        <form onSubmit={submit} className="bo-card flex flex-col gap-4 p-5">
          <div className="grid grid-cols-2 gap-3">
            {visible.map((k) => (
              <button type="button" key={k} onClick={() => { setOp(k); setPkg(null); }} data-testid={`topup-operator-${k}`}
                className={`flex items-center gap-3 rounded-2xl border-2 p-4 transition-colors ${op === k ? "border-bo-green bg-bo-green/5" : "border-gray-200"}`}>
                <span className={`grid h-12 w-14 shrink-0 place-items-center rounded-xl text-sm font-extrabold text-white ${OPS[k].color}`}>{OPS[k].name}</span>
                <span className="font-bold">Recarga {OPS[k].name}</span>
              </button>
            ))}
          </div>
          {!o.can_order && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="topup-operator-unavailable">{o.unavailable_notice}</p>}
          <Field label="Número de telemóvel (São Tomé e Príncipe)"><input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} data-testid="topup-phone-input" /></Field>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Valor</p>
            {!pkgs.length ? <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="topup-no-packages">Sem valores disponíveis de momento.</p> : (
              <div className="flex flex-wrap gap-2">{pkgs.map((p) => (
                <button type="button" key={p.id} onClick={() => setPkg(p.id)} data-testid={`topup-package-${p.amount}`}
                  className={`rounded-2xl border-2 px-4 py-2 text-sm transition-colors ${pkg === p.id ? "border-bo-green bg-bo-green text-white" : "border-gray-200"}`}>
                  <strong>{fmt(p.amount)}</strong>{p.label && ` · ${p.label}`}<br /><span className={`text-xs ${pkg === p.id ? "font-semibold text-white" : "text-gray-600"}`}>Pagar {fmt(p.price)}</span></button>))}</div>
            )}
          </div>
          {sel && <p className="text-sm" data-testid="topup-price-summary">Preço a pagar (calculado pelo servidor ao criar o pedido): <strong>{fmt(sel.price)}</strong></p>}
          <OperatorInfo o={o} />
          {!user ? <Link to="/entrar?next=/recargas" className={`${btnPrimary} w-fit`} data-testid="topup-login-btn">Entrar para pedir recarga</Link> :
            <button disabled={!sel || !o.can_order || !user.email_verified || busy} className={`${btnPrimary} w-fit`} data-testid="topup-submit-btn"><Smartphone className="h-4 w-4" />{cfg.demo ? "Criar pedido DEMO" : "Pedir recarga"}</button>}
        </form>
      )}
      {mine.topups.length > 0 && <h2 className="font-bold">Os meus pedidos de recarga</h2>}
      {mine.topups.map((t) => <TopupRow key={t.id} t={t} transfer={mine.transfer} reload={loadMine} demo={!!cfg?.demo} />)}
      <p className="text-xs text-gray-500">Sem integração oficial com as operadoras: cada recarga é executada manualmente e só é concluída após confirmação operacional.</p>
    </div>
  );
}
